import React, { useContext, useEffect, useState } from 'react';
import { AppCtx } from '../AppContext';
import { http } from '../api';
import { X, Check, ShieldCheck } from '@phosphor-icons/react';
import { toast } from 'sonner';

const ROLE_BADGES = {
  facilitator: { label: 'Facilitator', cls: 'bg-white text-slate-700 border-slate-300' },
  executive: { label: 'Executive', cls: 'bg-white text-slate-700 border-slate-300' },
  participant: { label: 'Participant', cls: 'bg-white text-slate-700 border-slate-300' },
  super_admin: { label: 'Admin', cls: 'bg-ink text-white border-ink' },
  admin: { label: 'Admin', cls: 'bg-ink text-white border-ink' },
  viewer: { label: 'Viewer', cls: 'bg-slate-100 text-slate-700 border-slate-300' },
};

export default function ProfileModal({ open, onClose }) {
  const { user, t, locale, setUser } = useContext(AppCtx);
  const [name, setName] = useState(user?.full_name || '');
  const [notif, setNotif] = useState(user?.notification_preferences || { email: true, product_updates: false });
  const [busy, setBusy] = useState(false);
  const [rolePreviewEnabled, setRolePreviewEnabled] = useState(false);

  useEffect(() => {
    if (!open || !user) return undefined;
    let active = true;
    http.get('/v1/demo/status')
      .then(({ data }) => { if (active) setRolePreviewEnabled(Boolean(data.role_preview_enabled)); })
      .catch(() => { if (active) setRolePreviewEnabled(false); });
    return () => { active = false; };
  }, [open, user]);

  if (!open || !user) return null;

  const switchRole = async (role) => {
    setBusy(true);
    try {
      const r = await http.post('/auth/role', { role });
      setUser(r.data);
      toast.success(t.profile.switchedTo.replace('{role}', t.profile[role] || role));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error');
    } finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const r = await http.patch('/auth/profile', { full_name: name, locale, notification_preferences: notif });
      setUser(r.data);
      toast.success(t.profile.saved);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error');
    } finally { setBusy(false); }
  };

  const badge = ROLE_BADGES[user.role] || ROLE_BADGES.viewer;

  return (
    <div
      data-testid="profile-modal"
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-3 pt-6 backdrop-blur-sm sm:items-center sm:p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="flex max-h-[calc(100dvh-2rem)] w-full max-w-lg flex-col overflow-hidden rounded-md border border-slate-200 bg-white shadow-2xl animate-fade-up">
        <div className="flex shrink-0 items-center justify-between border-b border-slate-200 p-4 sm:p-5">
          <div>
            <div className="font-heading text-lg font-bold tracking-tight">{t.profile.title}</div>
          </div>
          <button type="button" data-testid="profile-close" onClick={onClose} className="text-slate-400 hover:text-ink" aria-label="Close profile"><X size={18} /></button>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-4 sm:p-5">
          <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-ink text-white flex items-center justify-center text-sm font-bold tracking-wider shrink-0">
              {(user.full_name || '').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="truncate font-semibold">{user.full_name}</div>
              <div className="truncate text-[11px] text-slate-500 font-mono">{user.email}</div>
            </div>
            <span className={`shrink-0 text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 border rounded-sm ${badge.cls}`}>{badge.label}</span>
          </div>

          <div>
            <label className="text-[11px] tracking-widest uppercase text-slate-500 font-bold">Name</label>
            <input data-testid="profile-name" value={name} onChange={e => setName(e.target.value)} className="tkp-input mt-2" />
          </div>

          <div>
            <label className="text-[11px] tracking-widest uppercase text-slate-500 font-bold">{t.profile.organization}</label>
            <input value={rolePreviewEnabled ? 'TALKTO+ Synthetic Demo Organization' : user.tenant_id} readOnly aria-readonly="true" className="tkp-input mt-2 bg-slate-50 text-slate-500" />
            <div className="mt-1 text-xs text-slate-500">{rolePreviewEnabled ? 'Synthetic demo organization. No real customer data is used.' : 'Organization identity is managed by your administrator.'}</div>
          </div>

          {rolePreviewEnabled && <div>
            <label className="text-[11px] tracking-widest uppercase text-slate-500 font-bold">Demo role preview</label>
            <div className="mt-1 flex items-start gap-2 rounded-sm border border-sky-200 bg-sky-50 px-3 py-2 text-xs leading-5 text-sky-900">
              <ShieldCheck size={14} weight="duotone" className="mt-0.5 shrink-0" />
              Role preview changes visible navigation and access for this demo session.
            </div>
            <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {[
                { k: 'facilitator', l: t.profile.facilitator },
                { k: 'participant', l: t.profile.participant || 'Participant' },
                { k: 'executive', l: t.profile.executive },
                { k: 'super_admin', l: t.profile.admin },
              ].map(r => (
                <button
                  type="button"
                  key={r.k}
                  data-testid={`role-${r.k}`}
                  disabled={busy}
                  onClick={() => switchRole(r.k)}
                  className={`min-h-[52px] min-w-0 px-3 py-2 text-[11px] font-bold tracking-widest uppercase leading-tight rounded-sm border transition-colors ${
                    user.role === r.k ? 'bg-ink text-white border-ink' : 'bg-white text-slate-600 border-slate-200 hover:border-ink'
                  }`}
                >
                  {r.l}
                </button>
              ))}
            </div>
          </div>}

          <div>
            <label className="text-[11px] tracking-widest uppercase text-slate-500 font-bold">{t.profile.notifications}</label>
            <div className="mt-2 space-y-2">
              {[
                { k: 'email', l: 'Email summaries' },
                { k: 'product_updates', l: 'Product updates' },
              ].map(o => (
                <label key={o.k} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    data-testid={`pref-${o.k}`}
                    type="checkbox"
                    checked={!!notif[o.k]}
                    onChange={e => setNotif({ ...notif, [o.k]: e.target.checked })}
                    className="w-4 h-4 accent-ink"
                  />
                  {o.l}
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="grid shrink-0 grid-cols-2 gap-2 border-t border-slate-200 p-4 sm:flex sm:items-center sm:justify-end sm:p-5">
          <button type="button" onClick={onClose} className="rounded-sm border border-slate-200 px-4 py-2 text-xs font-bold uppercase tracking-widest hover:border-ink">{t.cancel}</button>
          <button type="button" data-testid="profile-save" onClick={save} disabled={busy} className="tkp-btn-primary flex items-center justify-center gap-2">
            <Check size={14} weight="bold" />{busy ? '...' : t.save}
          </button>
        </div>
      </div>
    </div>
  );
}
